import request from './request'
import { getMockCouponCreateResponse, shouldUseCouponMock } from './couponCenterMock'
import type { CouponDetailData } from './CouponDetai'
import type { UploadResponse } from './UpLoad'

export interface ApiResponse<T = unknown> {
  code: number
  data: T
  msg: string
}

export interface CouponCreateOption {
  label: string
  value: string
}

/** Form submit payload from `CreateCoupon.vue` */
export interface CouponCreateParams {
  name: string
  code: string
  picture?: File | null
  /** 复制/已有图：相对 path 或完整 URL，无新 File 时传这个 */
  picture_url?: string
  discount_type: string
  discount_amount: string
  min_spend_not_required: boolean
  min_spend: string
  start_type: string
  start_time?: string
  end_type: string
  end_time?: string
  eligible_services: string[]
  usage_limit: string
  eligible_regions: string
  max_redemptions?: string
  unlimited_redemptions: boolean
  description: string
}

/** Request body for `POST /api/admin/coupon/create` */
export interface CouponCreateBody {
  name: string
  code: string
  picture_url: string
  discount_type: string
  discount_value: number
  min_spend_not_required: boolean
  min_spend_amount: number
  start_mode: string
  start_time: string
  end_mode: string
  end_time: string
  eligible_services: string[]
  usage_limit_type: string
  eligible_regions: string
  total_redemptions_unlimited: boolean
  total_redemptions_limit: number
  description: string
}

export type CouponCreateData = CouponDetailData

export const DEFAULT_DISCOUNT_TYPES: CouponCreateOption[] = [
  { value: 'fixed_amount', label: 'Fixed Amount' },
  { value: 'percentage_off', label: 'Percentage Off' },
]

export const DEFAULT_START_VALIDITY_TYPES: CouponCreateOption[] = [
  { value: 'immediately', label: 'Immediately' },
  { value: 'specific_time', label: 'From a specific time' },
]

export const DEFAULT_END_VALIDITY_TYPES: CouponCreateOption[] = [
  { value: 'specific_time', label: 'Until a specific time' },
  { value: 'never_expires', label: 'Never expires' },
]

export const DEFAULT_ELIGIBLE_SERVICE_OPTIONS: CouponCreateOption[] = [
  { value: 'all_services', label: 'All services' },
  { value: 'essential_care_subscription', label: 'Essential Care Subscription' },
  { value: 'enhanced_care_subscription', label: 'Enhanced Care Subscription' },
  { value: 'premium_care_subscription', label: 'Premium Care Subscription' },
]

export const DEFAULT_USAGE_LIMIT_OPTIONS: CouponCreateOption[] = [
  { value: 'one_per_account_or_address', label: 'One-time use per account or address' },
  { value: 'one_per_address', label: 'One-time use per address' },
  { value: 'no_limit', label: 'No usage limit' },
]

export const DEFAULT_ELIGIBLE_REGION_OPTIONS: CouponCreateOption[] = [
  { value: 'one_per_account_or_address', label: 'One-time use per account or address' },
  { value: 'one_per_address', label: 'One-time use per address' },
  { value: 'no_limit', label: 'No usage limit' },
]

function parseNumericValue(value: string): number {
  const normalized = value.replace(/[^0-9.]/g, '')
  const parsed = Number.parseFloat(normalized)
  return Number.isNaN(parsed) ? 0 : parsed
}

function formatDateTimeForApi(value?: string): string {
  const raw = (value ?? '').trim()
  if (!raw) return ''

  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return raw

  const pad = (part: number) => String(part).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

function mapDiscountTypeForApi(value: string): string {
  const normalized = value.trim().toLowerCase()
  // Backend expects `percentage_off` (not `percentage`) — otherwise create returns 400.
  if (
    normalized === 'percentage_off' ||
    normalized === 'percentage' ||
    normalized === '2' ||
    normalized.includes('percent')
  ) {
    return 'percentage_off'
  }
  return 'fixed_amount'
}

function mapEndModeForApi(endType: string): string {
  return endType === 'never_expires' ? 'never_expires' : 'specific_time'
}

function mapEligibleServicesForApi(services: string[]): string[] {
  return services.map((service) => {
    const normalized = service.trim().toLowerCase()
    if (normalized === 'all_services' || normalized === '2') return 'all'
    return service
  })
}

/** Usage Limit：第三档后端存 `unlimited`（传 `no_limit` 会被归一成账户/地址） */
function mapUsageLimitForApi(value: string): string {
  if (value === 'no_limit') return 'unlimited'
  return value
}

/** Eligible Regions：第三档传 `no_limit`（`unlimited` 也能存，但文案侧仍常错） */
function mapEligibleRegionsForApi(value: string): string {
  if (value === 'unlimited') return 'no_limit'
  return value || 'one_per_account_or_address'
}

/** 详情展示 / 复制提交：统一成后端可用的相对 path（正斜杠） */
export function formatCouponPicturePathForApi(value?: string): string {
  const trimmed = (value ?? '').trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('blob:')) return ''

  let pathname = trimmed.replace(/\\/g, '/')
  if (/^https?:\/\//i.test(pathname)) {
    try {
      pathname = new URL(pathname).pathname
    } catch {
      /* keep pathname */
    }
  }

  const base = (import.meta.env.VITE_BASE_URL || '').replace(/\/$/, '')
  if (base && pathname.startsWith(base)) {
    pathname = pathname.slice(base.length)
  }
  if (pathname.startsWith('/storage/')) {
    pathname = pathname.slice('/storage'.length)
  }
  return pathname.replace(/^\/+/, '').replace(/\/{2,}/g, '/')
}

function isUploadSuccess(response: UploadResponse): boolean {
  const msg = (response.msg ?? '').trim().toLowerCase()
  if (response.code === 200) return true
  return msg === 'successfully.' || msg.includes('uploaded successfully')
}

function readUploadedPicturePath(data: UploadResponse['data'] | null | undefined): string {
  if (!data) return ''
  const raw =
    (typeof data.path === 'string' && data.path.trim()) ||
    (typeof data.url === 'string' && data.url.trim()) ||
    (typeof data.file === 'string' && data.file.trim()) ||
    ''
  return formatCouponPicturePathForApi(raw)
}

/** 与技师头像一致：先 upload 拿 path */
export async function uploadCouponPicture(file: File): Promise<string> {
  const formData = new FormData()
  formData.append('category', 'coupon')
  formData.append('file', file)

  const response = await request.post<UploadResponse>('/api/admin/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  if (!isUploadSuccess(response)) {
    throw new Error(response.msg || 'Failed to upload coupon picture')
  }

  const path = readUploadedPicturePath(response.data)
  if (!path) {
    throw new Error('Upload succeeded but no picture path was returned')
  }
  return path
}

export function buildCouponCreateBody(
  params: CouponCreateParams,
  pictureUrl = '',
): CouponCreateBody {
  const minSpendNotRequired = params.min_spend_not_required
  const unlimitedRedemptions = params.unlimited_redemptions

  return {
    name: params.name.trim(),
    code: params.code.trim(),
    picture_url: formatCouponPicturePathForApi(pictureUrl || params.picture_url),
    discount_type: mapDiscountTypeForApi(params.discount_type),
    discount_value: parseNumericValue(params.discount_amount),
    min_spend_not_required: minSpendNotRequired,
    min_spend_amount: minSpendNotRequired ? 0 : parseNumericValue(params.min_spend),
    start_mode: params.start_type,
    start_time:
      params.start_type === 'specific_time' ? formatDateTimeForApi(params.start_time) : '',
    end_mode: mapEndModeForApi(params.end_type),
    end_time: params.end_type === 'specific_time' ? formatDateTimeForApi(params.end_time) : '',
    eligible_services: mapEligibleServicesForApi(params.eligible_services),
    usage_limit_type: mapUsageLimitForApi(params.usage_limit),
    eligible_regions: mapEligibleRegionsForApi(params.eligible_regions),
    total_redemptions_unlimited: unlimitedRedemptions,
    total_redemptions_limit: unlimitedRedemptions
      ? 0
      : parseNumericValue(params.max_redemptions ?? ''),
    description: params.description.trim(),
  }
}

/** POST `/api/admin/coupon/create` — 图片先 upload，再 JSON 带 picture_url */
export async function postCouponCreate(
  params: CouponCreateParams,
): Promise<ApiResponse<CouponCreateData>> {
  let pictureUrl = formatCouponPicturePathForApi(params.picture_url)

  if (params.picture) {
    pictureUrl = await uploadCouponPicture(params.picture)
  }

  const body = buildCouponCreateBody(params, pictureUrl)

  if (shouldUseCouponMock()) {
    return getMockCouponCreateResponse(
      body as unknown as Record<string, unknown>,
    ) as unknown as ApiResponse<CouponCreateData>
  }

  return request.post<ApiResponse<CouponCreateData>>('/api/admin/coupon/create', body)
}
